import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Zap, 
  Sparkles, 
  ShieldCheck, 
  Users, 
  Code, 
  Rocket, 
  Plus, 
  Bug, 
  RefreshCw,
  Cpu,
  Cloud,
  Layers,
  History
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const changelogData = [
  {
    version: "v2.4.0",
    date: "February 10, 2026",
    title: "The Intelligence Update",
    type: "major",
    items: [
      { type: "feature", title: "Gemini AI Task Extraction", desc: "Automatically transform PDF project briefs into actionable Kanban cards with one click.", icon: Sparkles },
      { type: "improvement", title: "Enhanced Credential Vault", desc: "Added role-based access logging and auto-expiry for shared secrets.", icon: ShieldCheck },
      { type: "feature", title: "Salesforce Two-Way Sync", desc: "Connect your SFDC Opportunities directly to development boards.", icon: Cloud }
    ]
  },
  {
    version: "v2.3.5",
    date: "January 25, 2026",
    title: "Collaboration Refresh",
    type: "minor",
    items: [
      { type: "improvement", title: "Real-time Presence", desc: "See exactly who is viewing a card with live avatar bubbles.", icon: Users },
      { type: "feature", title: "Rich Text Comments", desc: "Full Markdown support and @mentions in all task discussions.", icon: Code },
      { type: "fix", title: "Socket.io Reconnection", desc: "Fixed a bug where updates would pause during network switching.", icon: Bug }
    ]
  },
  {
    version: "v2.3.0",
    date: "December 15, 2025",
    title: "Organization First",
    type: "major",
    items: [
      { type: "feature", title: "Project Categories", desc: "Group boards by client, department, or custom labels.", icon: Layers },
      { type: "improvement", title: "Global Search", desc: "Blazing fast search across all tasks, descriptions, and attachments.", icon: Zap },
      { type: "improvement", title: "Archived Project Recovery", desc: "Restore deleted projects with full history preservation.", icon: History }
    ]
  }
];

const ChangelogPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-[60vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400 rounded-full blur-[120px] -mr-40 -mt-40" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 rounded-full blur-[100px] -ml-20 -mb-20" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow">Product Updates</span>
              <h1 className="text-navy mt-6 mb-8">
                What's New in <span className="text-blue-600">PMS.</span>
              </h1>
              <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
                Stay up to date with the latest features, improvements, and fixes we've built to help your team ship faster.
              </p>
              <div className="flex justify-center gap-4">
                <a href="#updates" onClick={(e) => handleNavClick(e, "updates")} className="btn-primary">
                  View Latest Updates
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <Link to="/contact" className="btn-secondary">
                  Subscribe to Newsletter
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Changelog Timeline */}
        <section id="updates" className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-20">
              {changelogData.map((release, idx) => (
                <div key={idx} className="relative reveal">
                  {/* Timeline marker */}
                  <div className="absolute -left-12 top-0 h-full w-0.5 bg-gray-100 hidden md:block">
                    <div className="absolute top-0 -left-2.5 w-6 h-6 rounded-full bg-blue-600 border-4 border-white shadow-sm" />
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest ${
                          release.type === 'major' ? 'bg-navy text-sky' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {release.version}
                        </span>
                        <span className="text-sm font-bold text-gray-400">{release.date}</span>
                      </div>
                      <h2 className="text-3xl font-black text-navy">{release.title}</h2>
                    </div>
                  </div>

                  <div className="grid gap-6">
                    {release.items.map((item, i) => (
                      <div key={i} className="group p-8 bg-white border border-gray-100 rounded-[32px] hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 transition-all">
                        <div className="flex items-start gap-6">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${
                            item.type === 'feature' ? 'bg-blue-50 text-blue-600' :
                            item.type === 'improvement' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'
                          }`}>
                            <item.icon className="w-7 h-7" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded ${
                                item.type === 'feature' ? 'bg-blue-100 text-blue-700' :
                                item.type === 'improvement' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {item.type}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                            <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Load More / Pagination Placeholder */}
            <div className="mt-20 text-center reveal">
              <button className="btn-secondary">
                View All History
                <History className="w-5 h-5 ml-2 text-navy" />
              </button>
            </div>
          </div>
        </section>

        {/* Commitment Section */}
        <section className="shell-sky py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Continuous Delivery</span>
                <h2 className="mt-4 mb-6 text-navy">Built at the Speed of <span className="text-blue-600">Thought.</span></h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-10">
                  Our engineering team at Leanport Software releases updates every two weeks. We don't just fix bugs; we constantly rethink how project management can be more intelligent and secure.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white/60 p-6 rounded-2xl border border-white/80">
                    <p className="text-3xl font-black text-navy mb-1">26</p>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Releases / Year</p>
                  </div>
                  <div className="bg-white/60 p-6 rounded-2xl border border-white/80">
                    <p className="text-3xl font-black text-navy mb-1">99.9%</p>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Uptime Record</p>
                  </div>
                </div>
              </div>
              <div className="reveal-right">
                <div className="bg-navy rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Cpu className="w-32 h-32 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-6 text-white">Upcoming in Q2 2026</h3>
                  <div className="space-y-6">
                    {[
                      "Multi-cloud storage mirroring",
                      "AI-powered resource allocation",
                      "Advanced Kanban automation scripts",
                      "Native mobile apps for iOS & Android"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-4 group cursor-default">
                        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-sky group-hover:bg-sky group-hover:text-navy transition-all">
                          <Plus className="w-4 h-4" />
                        </div>
                        <span className="text-blue-100 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-white mb-8">Ready to see what else we can do?</h2>
            <p className="text-xl text-blue-100 mb-12">
              Join the thousands of teams scaling their productivity with PMS.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing" className="btn-secondary">
                See Pricing
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default ChangelogPage;
